// /exchange-1c/events — полный экран поиска по аудит-журналу 1С-обмена.
// Доступ — distributor.integration.1c.view-sync-events (как и /exchange-1c).
//
// URL-state: все фильтры сериализуются в query-params, чтобы ссылку можно было
// сохранить/расшарить. Multi-select поля повторяют ключ
// (`?type=catalog&type=sale`); single-value — обычные `?requestId=req-42`.
// validateSearch нормализует TanStack'овский raw record (string | string[]) к
// массивам перед zod-парсингом — аналогично `orders.index.tsx` DO-5.

import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { RequireAuth } from '@dss/auth'
import {
  SyncEventsFullSearchScreen,
  type SyncEventsFullSearchInitial,
} from '../features/exchange-1c/SyncEventsFullSearchScreen'

const responseStatusSchema = z.enum([
  'success',
  'failure',
  'progress',
  'in_flight',
  'aborted',
])

const eventsSearchSchema = z
  .object({
    responseStatus: z.array(responseStatusSchema).optional(),
    type: z.array(z.string()).optional(),
    mode: z.array(z.string()).optional(),
    errorCategory: z.array(z.string()).optional(),
    since: z.string().optional(),
    until: z.string().optional(),
    requestId: z.string().optional(),
    errorCodePrefix: z.string().optional(),
    hasWarnings: z.coerce.boolean().optional(),
    durationMsMin: z.coerce.number().int().nonnegative().optional(),
    durationMsMax: z.coerce.number().int().nonnegative().optional(),
  })
  .partial()

type EventsSearch = z.infer<typeof eventsSearchSchema>

export const Route = createFileRoute('/exchange-1c/events')({
  validateSearch: (search): EventsSearch => {
    // TanStack-router отдаёт сырой record-объект (string | string[] | undefined).
    // Multi-value поля нужно нормализовать к массиву ДО zod-парсинга, иначе
    // одиночный `?type=catalog` упадёт на `z.array()`.
    const MULTI_VALUE_KEYS: ReadonlyArray<string> = [
      'responseStatus',
      'type',
      'mode',
      'errorCategory',
    ]
    const normalized: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(search as Record<string, unknown>)) {
      if (MULTI_VALUE_KEYS.includes(key)) {
        normalized[key] = Array.isArray(value) ? value : value != null ? [value] : value
      } else {
        normalized[key] = value
      }
    }
    return eventsSearchSchema.parse(normalized)
  },
  component: SyncEventsFullSearchRoute,
})

function SyncEventsFullSearchRoute() {
  const search = Route.useSearch()
  const initial: SyncEventsFullSearchInitial = {
    responseStatus: search.responseStatus,
    type: search.type,
    mode: search.mode,
    errorCategory: search.errorCategory,
    since: search.since,
    until: search.until,
    requestId: search.requestId,
    errorCodePrefix: search.errorCodePrefix,
    hasWarnings: search.hasWarnings,
    durationMsMin: search.durationMsMin,
    durationMsMax: search.durationMsMax,
  }
  return (
    <RequireAuth>
      <SyncEventsFullSearchScreen initialFilters={initial} />
    </RequireAuth>
  )
}
